import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Briefcase,
  CalendarDays,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  X,
  Timer,
  Trophy,
  Users,
} from 'lucide-react';
import Brand from '../components/Brand';
import ModeToggle from '../components/ModeToggle';
import NotificationCenter from '../components/NotificationCenter';
import TimerWidget from '../components/TimerWidget';
import { closeSocket } from '../hooks/useSocket';
import { clearSession, getUser } from '../lib/auth';
import { getLang, setLang, t } from '../lib/i18n';

const NAV = [
  { to: '/', label: 'Projects', icon: Briefcase, end: true },
  { to: '/my-work', label: 'My Work', icon: LayoutDashboard },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/time', label: 'Time', icon: Timer },
  { to: '/checkins', label: 'Check-ins', icon: ClipboardCheck },
  { to: '/points', label: 'Points', icon: Trophy },
  // team-wide view — not part of the Contributor surface
  { to: '/workloads', label: 'Workloads', icon: Users, hideForContributor: true },
  { to: '/reports', label: 'Reports', icon: BarChart3, adminOnly: true },
];

export default function AppShell() {
  const navigate = useNavigate();
  const user = getUser();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false); // phone/tablet drawer
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const logout = () => {
    clearSession();
    closeSocket();
    navigate('/login', { replace: true });
  };

  // Navy command sidebar: light text on navy, teal left-border + subtle wash when
  // active (the IMS/Velzon register). Hardcoded because the navy is a fixed brand
  // zone, not a var(--pm-*) surface.
  const navStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: 'none',
    color: isActive ? '#ffffff' : '#abb9e8',
    background: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
    borderLeft: `3px solid ${isActive ? 'var(--pm-accent)' : 'transparent'}`,
  });

  return (
    <div className="flex h-screen">
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-56 shrink-0 flex-col bg-pm-sidebar transition-transform duration-200 motion-reduce:transition-none lg:static lg:translate-x-0 ${navOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}>
        <div style={{ color: '#fff' }} className="px-4 py-4">
          <Brand size="sm" linkToHome />
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2">
          {NAV.filter((n) => (!n.adminOnly || user?.role === 'admin')
            && !(n.hideForContributor && user?.pr === 'contributor')).map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} style={navStyle}>
              <Icon size={16} aria-hidden />
              {t(label)}
            </NavLink>
          ))}
        </nav>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div style={{ color: '#fff' }} className="text-sm">{user?.name || t('Signed in')}</div>
              <div style={{ color: '#abb9e8' }} className="text-xs capitalize">{t(user?.pr || user?.role || '')}</div>
            </div>
            <button
              type="button"
              onClick={() => setLang(getLang() === 'es' ? 'en' : 'es')}
              style={{ color: '#abb9e8', borderColor: 'rgba(255,255,255,0.2)' }}
              className="rounded-md border px-2.5 py-2 text-[11px] font-semibold uppercase hover:text-white lg:px-1.5 lg:py-0.5 lg:text-[10px]"
              title="EN / ES"
            >
              {getLang() === 'es' ? 'ES' : 'EN'}
            </button>
          </div>
        </div>
      </aside>
      {navOpen && <div className="fixed inset-0 z-40 bg-black/45 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-pm-border bg-pm-topbar px-3 sm:px-4">
          <button type="button" onClick={() => setNavOpen((o) => !o)} aria-label={t('Menu')} aria-expanded={navOpen}
            className="-ml-1 rounded-md p-2.5 text-pm-muted hover:bg-pm-raised hover:text-pm-text lg:hidden">
            {navOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
          </button>
          <label className="relative hidden min-w-0 flex-1 items-center sm:flex" style={{ maxWidth: 360 }}>
            <Search size={15} aria-hidden className="pointer-events-none absolute left-2.5 text-pm-faint" />
            <input
              type="search"
              placeholder={t('Search')}
              aria-label={t('Search')}
              className="w-full rounded-md border border-pm-border bg-pm-input py-1.5 pl-8 pr-3 text-sm text-pm-text placeholder:text-pm-faint outline-none transition-colors focus:border-[var(--pm-focus-border)]"
            />
          </label>
          <div className="ml-auto flex items-center gap-1">
          <ModeToggle />
          <TimerWidget />
          <NotificationCenter />
          <button
            type="button"
            onClick={logout}
            title={t('Sign out')}
            className="rounded-md p-2.5 text-pm-muted hover:bg-pm-raised hover:text-pm-text lg:p-2"
          >
            <LogOut size={16} aria-hidden />
            <span className="sr-only">{t('Sign out')}</span>
          </button>
          </div>
        </header>
        <main className="min-h-0 min-w-0 flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
